import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Button,
  makeStyles,
  withStyles,
  Paper,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import clsx from 'clsx';
import axios from 'axios';
import { BASE_API, USER_TOKEN } from 'config/consts';
import { toastr } from 'react-redux-toastr';
import StepDadosPessoais from './StepDadosPessoais';
import StepModalidades from './StepModalidades';
import StepSarau from './StepSarau';
import StepHospedagem from './StepHospedagem';
import StepTermos from './StepTermos';
import StepConfirmacao from './StepConfirmacao';
import {
  assignCompanionsToRooms,
  buildDynamicSelections,
  calculateAccommodationTotals,
  countSelectedSarau,
  countSelectedSports,
  formatPhone,
  getSarauFieldKeys,
  getSportFieldKeys,
  isPartnerPerCategory,
  isValidBirthday,
  parseCopaSarauActivityConfig,
  resolveActiveRoomRules,
} from './config';

const STEPS = [
  'Dados',
  'Esportes',
  'Sarau',
  'Hospedagem',
  'Termos',
  'Pagamento',
];

const isHotelSelectable = (hotel) =>
  !!hotel &&
  hotel.active !== false &&
  (!hotel.isLimited ||
    (typeof hotel.remaining === 'number' && hotel.remaining > 0));

const GreenConnector = withStyles({
  alternativeLabel: { top: 18 },
  active: { '& $line': { borderColor: '#4caf50' } },
  completed: { '& $line': { borderColor: '#4caf50' } },
  line: { borderColor: '#e0e0e0', borderTopWidth: 3 },
})(StepConnector);

const useStepIconStyles = makeStyles({
  root: {
    backgroundColor: '#e0e0e0',
    zIndex: 1,
    color: '#fff',
    width: 40,
    height: 40,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 14,
    fontWeight: 'bold',
  },
  active: {
    backgroundColor: '#1a3a5c',
    boxShadow: '0 2px 8px rgba(0,0,0,.25)',
  },
  completed: { backgroundColor: '#4caf50' },
});

function StepIcon(props) {
  const classes = useStepIconStyles();
  const { active, completed, icon } = props;
  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
        [classes.completed]: completed,
      })}
    >
      {completed ? <CheckCircleIcon style={{ fontSize: 24 }} /> : icon}
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 800,
    margin: '0 auto',
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,.08)',
  },
  stepper: {
    backgroundColor: 'transparent',
    padding: theme.spacing(2, 0),
  },
  stepLabel: {
    fontSize: 11,
    marginTop: '4px !important',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  btnPrimary: {
    backgroundColor: '#1a3a5c',
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: 8,
    padding: '10px 32px',
    textTransform: 'none',
    '&:hover': { backgroundColor: '#15304d' },
    '&:disabled': { backgroundColor: '#ccc' },
  },
  btnOutline: {
    borderColor: '#1a3a5c',
    color: '#1a3a5c',
    fontWeight: 'bold',
    borderRadius: 8,
    padding: '10px 32px',
    textTransform: 'none',
  },
}));

function CopaSarauForm(props) {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [availability, setAvailability] = useState(null);
  const activity = props.activityData || props.activities?.detail || {};
  const user = props.userData || props.auth?.user || {};
  const activityConfig = useMemo(
    () => parseCopaSarauActivityConfig(activity),
    [activity]
  );
  const initialSelections = useMemo(
    () => buildDynamicSelections(activityConfig),
    [activityConfig]
  );
  const initialTerms = useMemo(
    () =>
      activityConfig.defaultTerms.reduce(
        (acc, term) => ({
          ...acc,
          [term.field]: false,
        }),
        {}
      ),
    [activityConfig]
  );

  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    birthday: '',
    city: '',
    state: '',
    phone: '',
    email: '',
    situation: '',
    organ: '',
    matricula: '',
    modalities: initialSelections.modalities,
    sarau_activities: initialSelections.sarauActivities,
    hotel_id: '',
    going_alone: false,
    room_share_name: '',
    companions: [],
    terms: initialTerms,
    custom_answers: [],
    payment_email: '',
    payment_whatsapp: '',
  });

  const normalizeStoredTerms = (storedTerms) => {
    if (Array.isArray(storedTerms)) {
      return storedTerms.reduce(
        (acc, term) => ({
          ...acc,
          [term]: true,
        }),
        {}
      );
    }

    if (storedTerms && typeof storedTerms === 'object') {
      return storedTerms;
    }

    return {};
  };

  const normalizeStoredCompanions = (storedCompanions) => {
    if (!Array.isArray(storedCompanions)) return [];

    return storedCompanions.map((companion) => ({
      name: companion.name || '',
      cpf: companion.cpf || '',
      birthday: companion.birthday || '',
      city: companion.city || '',
      state: companion.state || '',
      phone: companion.phone || '',
      email: companion.email || '',
      is_minor: !!companion.is_minor,
      room_number: companion.room_number || 1,
      modalities: companion.modalities || {},
      sarau_activities: companion.sarau_activities || {},
    }));
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      modalities: {
        ...initialSelections.modalities,
        ...prev.modalities,
      },
      sarau_activities: {
        ...initialSelections.sarauActivities,
        ...prev.sarau_activities,
      },
      terms: {
        ...initialTerms,
        ...prev.terms,
      },
    }));
  }, [initialSelections, initialTerms]);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || '',
        cpf: user.cpf || '',
        birthday: user.birthday
          ? user.birthday.includes('T')
            ? user.birthday.substring(0, 10).split('-').reverse().join('/')
            : user.birthday
          : '',
        city: user.city || '',
        state: user.state || '',
        phone: formatPhone(user.tel || ''),
        email: user.email || '',
        payment_email: user.email || '',
        payment_whatsapp: formatPhone(user.tel || ''),
      }));
    }
  }, [user]);

  useEffect(() => {
    if (activity && activity.id) {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      axios
        .get(`${BASE_API}/copa-sarau/registration/${activity.id}`, {
          headers: { Authorization: token },
        })
        .then((res) => {
          if (res.data.registration) {
            const registration = res.data.registration;
            setExistingRegistration(registration);
            setFormData((prev) => ({
              ...prev,
              name: registration.name || prev.name,
              cpf: registration.cpf || prev.cpf,
              birthday: registration.birthday || prev.birthday,
              city: registration.city || prev.city,
              state: registration.state || prev.state,
              phone: formatPhone(registration.phone || prev.phone),
              email: registration.email || prev.email,
              situation: registration.situation || prev.situation,
              organ: registration.organ || prev.organ,
              matricula: registration.matricula || prev.matricula,
              modalities: {
                ...initialSelections.modalities,
                ...(registration.modalities || {}),
              },
              sarau_activities: {
                ...initialSelections.sarauActivities,
                ...(registration.sarau_activities || {}),
              },
              hotel_id: registration.hotel_id || prev.hotel_id || '',
              going_alone: !!registration.going_alone,
              room_share_name: registration.room_share_name || '',
              companions: normalizeStoredCompanions(registration.companions),
              terms: {
                ...initialTerms,
                ...normalizeStoredTerms(registration.terms_accepted),
              },
              custom_answers: Array.isArray(registration.custom_answers)
                ? registration.custom_answers
                : [],
              payment_email:
                registration.payment_email ||
                registration.email ||
                prev.payment_email,
              payment_whatsapp: formatPhone(
                registration.payment_whatsapp ||
                  registration.phone ||
                  prev.payment_whatsapp
              ),
            }));
            if (registration.status === 'confirmed') {
              setSubmitted(true);
            }
          }
        })
        .catch(() => {});
    }
  }, [activity, initialSelections, initialTerms]);

  useEffect(() => {
    if (!activity || !activity.id) return;
    const token = JSON.parse(localStorage.getItem(USER_TOKEN));
    axios
      .get(`${BASE_API}/copa-sarau/room-availability`, {
        params: { action_id: activity.id },
        headers: { Authorization: token },
      })
      .then((res) => {
        if (res.data) setAvailability(res.data);
      })
      .catch(() => setAvailability(null));
  }, [activity, activeStep]);

  // Pré-seleciona o primeiro hotel com vagas. Mantém a escolha do usuário se
  // ele já tiver selecionado algo válido.
  useEffect(() => {
    const hotels = availability?.hotels || [];
    if (hotels.length === 0) return;
    setFormData((prev) => {
      const stillValid = hotels.find(
        (h) => h.id === prev.hotel_id && isHotelSelectable(h)
      );
      if (stillValid) return prev;
      const firstAvailable = hotels.find(isHotelSelectable);
      return { ...prev, hotel_id: firstAvailable ? firstAvailable.id : '' };
    });
  }, [availability, formData.hotel_id]);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedFormData = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  // Validações por step
  const validateStep = (step) => {
    switch (step) {
      case 0: {
        const { name, cpf, birthday, city, state, phone, email } = formData;
        if (!name || !cpf || !birthday || !city || !state || !phone || !email) {
          toastr.warning('Atenção', 'Preencha todos os campos obrigatórios');
          return false;
        }
        // Validar CPF básico
        const cpfClean = cpf.replace(/\D/g, '');
        if (cpfClean.length !== 11) {
          toastr.warning('Atenção', 'CPF inválido');
          return false;
        }
        if (!isValidBirthday(birthday)) {
          toastr.warning('Atenção', 'Data de nascimento inválida');
          return false;
        }
        const customQuestions = activityConfig.customQuestions || [];
        for (const question of customQuestions) {
          const answer = (formData.custom_answers || []).find(
            (a) => a.id === question.id
          );
          const value = answer ? answer.answer : null;
          const isEmpty =
            value === null ||
            value === undefined ||
            value === '' ||
            (Array.isArray(value) && value.length === 0);
          if (isEmpty) {
            toastr.warning(
              'Atenção',
              `Responda a pergunta: ${question.text}`
            );
            return false;
          }
        }
        return true;
      }
      case 1: {
        const m = formData.modalities;
        const anySelected = activityConfig.sports.some((sport) => m[sport.id]);
        if (!anySelected) {
          toastr.warning('Atenção', 'Selecione pelo menos uma opção');
          return false;
        }

        const selectedSportsCount = countSelectedSports(
          m,
          activityConfig.sports
        );
        if (selectedSportsCount > activityConfig.sportsLimit) {
          toastr.warning(
            'Atenção',
            `Selecione no máximo ${activityConfig.sportsLimit} modalidades`
          );
          return false;
        }

        for (const sport of activityConfig.sports) {
          if (!m[sport.id]) continue;

          const keys = getSportFieldKeys(sport);

          if (sport.shirt.enabled && sport.shirt.required && !m[keys.shirt]) {
            toastr.warning(
              'Atenção',
              `Selecione o tamanho da camisa para ${sport.name}`
            );
            return false;
          }

          if (sport.categories.enabled) {
            const categoriesValue = m[keys.categories];
            const hasCategories = sport.categories.multiple
              ? Array.isArray(categoriesValue) && categoriesValue.length > 0
              : !!categoriesValue;

            if (sport.categories.required && !hasCategories) {
              toastr.warning(
                'Atenção',
                `Selecione a categoria para ${sport.name}`
              );
              return false;
            }

            if (
              sport.categories.multiple &&
              Array.isArray(categoriesValue) &&
              categoriesValue.length > (sport.categories.maxSelection || 2)
            ) {
              toastr.warning(
                'Atenção',
                `Selecione no máximo ${
                  sport.categories.maxSelection || 2
                } categoria(s) para ${sport.name}`
              );
              return false;
            }
          }

          if (sport.partner_name.enabled && sport.partner_name.required) {
            const partnerValue = m[keys.partner];
            if (isPartnerPerCategory(sport)) {
              const selectedCategories = Array.isArray(m[keys.categories])
                ? m[keys.categories]
                : [];
              const missing = selectedCategories.find(
                (category) =>
                  !partnerValue ||
                  typeof partnerValue !== 'object' ||
                  !partnerValue[category]
              );
              if (selectedCategories.length === 0 || missing) {
                toastr.warning(
                  'Atenção',
                  `Informe o parceiro para ${sport.name}${
                    missing ? ` (${missing})` : ''
                  }`
                );
                return false;
              }
            } else if (!partnerValue) {
              toastr.warning(
                'Atenção',
                `Informe o parceiro para ${sport.name}`
              );
              return false;
            }
          }
        }

        return true;
      }
      case 2: {
        const s = formData.sarau_activities;
        const anySelected = activityConfig.sarau.some((item) => s[item.id]);
        if (!anySelected) {
          toastr.warning('Atenção', 'Selecione pelo menos uma opção');
          return false;
        }

        const selectedSarauCount = countSelectedSarau(s, activityConfig.sarau);
        if (selectedSarauCount > activityConfig.sarauLimit) {
          toastr.warning(
            'Atenção',
            `Selecione no máximo ${activityConfig.sarauLimit} atividades`
          );
          return false;
        }

        for (const item of activityConfig.sarau) {
          if (!s[item.id]) continue;

          const keys = getSarauFieldKeys(item);
          if (
            item.extra_text.enabled &&
            item.extra_text.required &&
            !s[keys.details]
          ) {
            toastr.warning(
              'Atenção',
              `Preencha o campo adicional para ${item.name}`
            );
            return false;
          }
        }

        return true;
      }
      case 3: {
        if (!availability) return true;
        const totals = calculateTotal();
        const requestedRooms = 1 + (totals.extraRooms || 0);
        const hotels = availability.hotels || [];
        const selectedHotel = hotels.find((h) => h.id === formData.hotel_id);
        const hasSelectableHotel = hotels.some(isHotelSelectable);

        if (hotels.length > 0 && !hasSelectableHotel) {
          toastr.warning(
            'Atenção',
            'Não há hotéis com quartos disponíveis no momento.'
          );
          return false;
        }

        if (hotels.length > 0 && !selectedHotel) {
          toastr.warning('Atenção', 'Selecione um hotel para continuar.');
          return false;
        }

        if (selectedHotel && !isHotelSelectable(selectedHotel)) {
          toastr.warning(
            'Atenção',
            `As vagas em ${selectedHotel.name} estão esgotadas. Escolha outro hotel.`
          );
          return false;
        }

        if (selectedHotel) {
          const maxPerReg = selectedHotel.max_rooms_per_registration;
          if (maxPerReg && maxPerReg > 0 && requestedRooms > maxPerReg) {
            toastr.warning(
              'Atenção',
              `${selectedHotel.name}: máximo de ${maxPerReg} quarto(s) por inscrição. Você está solicitando ${requestedRooms}.`
            );
            return false;
          }
          if (
            selectedHotel.isLimited &&
            typeof selectedHotel.remaining === 'number' &&
            requestedRooms > selectedHotel.remaining
          ) {
            toastr.warning(
              'Atenção',
              selectedHotel.remaining <= 0
                ? `As vagas em ${selectedHotel.name} estão esgotadas.`
                : `Não há quartos disponíveis em ${selectedHotel.name} para a quantidade solicitada.`
            );
            return false;
          }
        }

        return true;
      }
      case 4: {
        const t = formData.terms;
        if (!t.aptidao_fisica || !t.regras_organizacao || !t.lgpd) {
          toastr.warning('Atenção', 'Aceite todos os termos obrigatórios');
          return false;
        }
        return true;
      }
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const calculateTotal = () => {
    const activeRoomRules = resolveActiveRoomRules(
      activityConfig,
      formData.hotel_id
    );
    const assignedCompanions = assignCompanionsToRooms(
      formData.companions,
      activeRoomRules
    );
    return calculateAccommodationTotals(assignedCompanions, activeRoomRules);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const totals = calculateTotal();
      const activeRoomRules = resolveActiveRoomRules(
        activityConfig,
        formData.hotel_id
      );
      const assignedCompanions = assignCompanionsToRooms(
        formData.companions,
        activeRoomRules
      );
      const payload = {
        action_id: activity.id,
        station_id:
          (user.station && user.station.id) ||
          user.station_id ||
          activity.station_id ||
          null,
        hotel_id: formData.hotel_id || null,
        name: formData.name,
        cpf: formData.cpf,
        birthday: formData.birthday,
        city: formData.city,
        state: formData.state,
        phone: formData.phone,
        email: formData.email,
        situation: formData.situation,
        organ: formData.organ,
        matricula: formData.matricula,
        modalities: formData.modalities,
        sarau_activities: formData.sarau_activities,
        going_alone: formData.going_alone,
        room_share_name: formData.room_share_name,
        companions: totals.normalizedCompanions.map((companion, index) => ({
          ...companion,
          room_number:
            assignedCompanions[index]?.room_number ||
            companion.room_number ||
            1,
        })),
        terms_accepted: formData.terms,
        custom_answers: formData.custom_answers || [],
        payment_email: formData.payment_email || formData.email,
        payment_whatsapp: formData.payment_whatsapp || formData.phone,
      };

      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const res = await axios.post(`${BASE_API}/copa-sarau/register`, payload, {
        headers: { Authorization: token },
      });

      if (res.data.success) {
        setExistingRegistration(res.data.registration);
        setSubmitted(true);
        if (totals.total === 0) {
          toastr.success('Sucesso', 'Inscrição confirmada com sucesso!');
        } else {
          toastr.success(
            'Sucesso',
            'Inscrição registrada! Aguardando pagamento.'
          );
        }
      }
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      const errorMessage =
        err.response?.data?.message || 'Erro ao processar inscrição';
      if (
        errorCode === 'ROOM_INVENTORY_EXHAUSTED' ||
        errorCode === 'ROOM_PER_REGISTRATION_EXCEEDED'
      ) {
        toastr.error('Hospedagem indisponível', errorMessage);
        setActiveStep(3);
        try {
          const token = JSON.parse(localStorage.getItem(USER_TOKEN));
          const res = await axios.get(
            `${BASE_API}/copa-sarau/room-availability`,
            {
              params: { action_id: activity.id },
              headers: { Authorization: token },
            }
          );
          if (res.data) setAvailability(res.data);
        } catch (_) {
          /* ignore refresh error */
        }
      } else {
        toastr.error('Erro', errorMessage);
      }
    }
    setSubmitting(false);
  };

  if (submitted && existingRegistration) {
    const isPaid = existingRegistration.status === 'confirmed';
    const hasToPay = existingRegistration.total_amount > 0;

    return (
      <Box className={classes.container}>
        <Paper className={classes.paper}>
          <Box textAlign="center" py={3}>
            {isPaid || !hasToPay ? (
              <>
                <Box mb={2}>
                  <span
                    role="img"
                    aria-label="comemoração"
                    style={{ fontSize: 48 }}
                  >
                    🎉
                  </span>
                </Box>
                <Typography
                  variant="h5"
                  style={{ fontWeight: 'bold', marginBottom: 8 }}
                >
                  Sua inscrição foi finalizada com sucesso!
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Sua participação no evento está garantida.
                </Typography>
              </>
            ) : (
              <>
                <Box mb={2}>
                  <span
                    role="img"
                    aria-label="envelope"
                    style={{ fontSize: 48 }}
                  >
                    ✉️
                  </span>
                </Box>
                <Typography
                  variant="h5"
                  style={{ fontWeight: 'bold', marginBottom: 8 }}
                >
                  Sua inscrição está quase finalizada!
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Para finalizar a sua inscrição, realize o pagamento do Link
                  que foi enviado por email e para seu WhatsApp!
                </Typography>
              </>
            )}
            <Box
              mt={4}
              p={2}
              style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                POLÍTICA DE CANCELAMENTO
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Em caso de desistência, comunicar o Sindilegis com 30 dias de
                antecedência.
                {activityConfig.cancellationPhone ? (
                  <>
                    <br />
                    Telefone: {activityConfig.cancellationPhone}
                  </>
                ) : null}
              </Typography>
            </Box>
            <Box mt={3}>
              <Button
                className={classes.btnPrimary}
                onClick={() =>
                  props.standalone
                    ? window.close()
                    : props.history.push('/homepage')
                }
              >
                {props.standalone ? 'Fechar' : 'Concluir'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <StepDadosPessoais
            formData={formData}
            updateFormData={updateFormData}
            customQuestions={activityConfig.customQuestions}
          />
        );
      case 1:
        return (
          <StepModalidades
            formData={formData}
            updateNestedFormData={updateNestedFormData}
            activityConfig={activityConfig}
          />
        );
      case 2:
        return (
          <StepSarau
            formData={formData}
            updateNestedFormData={updateNestedFormData}
            activityConfig={activityConfig}
          />
        );
      case 3:
        return (
          <StepHospedagem
            formData={formData}
            updateFormData={updateFormData}
            activity={activity}
            calculateTotal={calculateTotal}
            activityConfig={activityConfig}
            availability={availability}
          />
        );
      case 4:
        return (
          <StepTermos
            formData={formData}
            updateNestedFormData={updateNestedFormData}
            activityConfig={activityConfig}
          />
        );
      case 5:
        return (
          <StepConfirmacao
            formData={formData}
            updateFormData={updateFormData}
            activity={activity}
            calculateTotal={calculateTotal}
            activityConfig={activityConfig}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box className={classes.container}>
      <Paper className={classes.paper}>
        <Stepper
          alternativeLabel
          activeStep={activeStep}
          connector={<GreenConnector />}
          className={classes.stepper}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={StepIcon}
                classes={{ label: classes.stepLabel }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box mt={2}>{renderStep()}</Box>

        <Box className={classes.buttonRow}>
          {activeStep > 0 ? (
            <Button
              variant="outlined"
              className={classes.btnOutline}
              onClick={handleBack}
            >
              Anterior
            </Button>
          ) : (
            <div />
          )}
          {activeStep < STEPS.length - 1 ? (
            <Button className={classes.btnPrimary} onClick={handleNext}>
              Próximo
            </Button>
          ) : (
            <Button
              className={classes.btnPrimary}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting
                ? 'Enviando...'
                : calculateTotal().total > 0
                ? 'Finalizar e Gerar Pagamento'
                : 'Confirmar Inscrição'}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  activities: state.activities,
  website: state.website,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CopaSarauForm);
